import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { TodayStudyTopic } from '../../types/StudyTopics';
import { logout } from '../auth/authSlice';
import { examUpdated } from '../exams/examsSlice';

type TopicsStatus = 'idle' | 'loading' | 'succeeded' | 'failed' | 'stale';

export interface TodayTopicsState {
  topics: TodayStudyTopic[];
  status: TopicsStatus;
  error: string | null;
  lastFetchedDate: string | null;
  lastFetchedExamId: string | null;
  lastFetchedSessionId: string | null;
  lastFetchedAt: number | null;
  /** Changes whenever a companion action may have changed topic filters. */
  topicFiltersRevision?: number;
}

const initialState: TodayTopicsState = {
  topics: [],
  status: 'idle',
  error: null,
  lastFetchedDate: null,
  lastFetchedExamId: null,
  lastFetchedSessionId: null,
  lastFetchedAt: null,
  topicFiltersRevision: 0,
};

// Cache expiration time: 30 minutes
const CACHE_EXPIRATION_MS = 30 * 60 * 1000;

const todayTopicsSlice = createSlice({
  name: 'todayTopics',
  initialState,
  reducers: {
    topicsLoading: (state) => {
      state.status = 'loading';
      state.error = null;
    },
    topicsReceived: (
      state,
      action: PayloadAction<{
        topics: TodayStudyTopic[];
        date: string;
        examId: string;
        sessionId: string;
      }>
    ) => {
      // De-duplicate topics based on their ID
      const uniqueTopics = action.payload.topics.reduce(
        (acc: TodayStudyTopic[], current: TodayStudyTopic) => {
          const isDuplicate = acc.some((topic) => topic.id === current.id);
          if (!isDuplicate) {
            acc.push(current);
          }
          return acc;
        },
        []
      );

      state.topics = uniqueTopics;
      state.status = 'succeeded';
      state.error = null;
      state.lastFetchedDate = action.payload.date;
      state.lastFetchedExamId = action.payload.examId;
      state.lastFetchedSessionId = action.payload.sessionId;
      state.lastFetchedAt = Date.now();
    },
    topicsFailed: (state, action: PayloadAction<string>) => {
      state.status = 'failed';
      state.error = action.payload;
    },
    // Mark topics as stale without clearing them
    markTopicsStale: (state) => {
      state.status = 'stale';
    },
    // Force invalidate - clears topics and forces refetch
    invalidateTopics: (state) => {
      state.topics = [];
      state.status = 'idle';
      state.lastFetchedDate = null;
      state.lastFetchedExamId = null;
      state.lastFetchedSessionId = null;
      state.lastFetchedAt = null;
      state.error = null;
    },
    topicFiltersRefreshRequested: (state) => {
      state.topicFiltersRevision = (state.topicFiltersRevision ?? 0) + 1;
    },
    clearTopics: (state) => {
      state.topics = [];
      state.status = 'idle';
      state.error = null;
      state.lastFetchedDate = null;
      state.lastFetchedExamId = null;
      state.lastFetchedSessionId = null;
      state.lastFetchedAt = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout, (state) => {
        state.topics = [];
        state.status = 'idle';
        state.error = null;
        state.lastFetchedDate = null;
        state.lastFetchedExamId = null;
        state.lastFetchedSessionId = null;
        state.lastFetchedAt = null;
        state.topicFiltersRevision = 0;
      })
      // Invalidate topics when exam settings change
      .addCase(examUpdated, (state, action) => {
        if (action.payload.primary === true) {
          state.status = 'stale';
        }
      });
  },
});

// Selectors
export const selectTodayTopics = (state: { todayTopics: TodayTopicsState }) =>
  state.todayTopics.topics || [];

export const selectTodayTopicsStatus = (state: {
  todayTopics: TodayTopicsState;
}) => state.todayTopics.status || 'idle';

export const selectTodayTopicsExamId = (state: {
  todayTopics: TodayTopicsState;
}) => state.todayTopics.lastFetchedExamId;

export const selectTopicFiltersRevision = (state: {
  todayTopics: TodayTopicsState;
}) => state.todayTopics.topicFiltersRevision ?? 0;

// Smart cache selector
export const selectShouldFetchTopics =
  (params: { date: string; examId: string; sessionId: string }) =>
  (state: { todayTopics: TodayTopicsState }) => {
    const {
      lastFetchedDate,
      lastFetchedExamId,
      lastFetchedSessionId,
      lastFetchedAt,
      status,
    } = state.todayTopics;

    const { date, examId, sessionId } = params;

    // Don't fetch if already loading
    if (status === 'loading') return false;

    // Always fetch if marked as stale
    if (status === 'stale') return true;

    // Fetch if different date
    if (lastFetchedDate !== date) return true;

    // Fetch if different exam
    if (lastFetchedExamId !== examId) return true;

    // Fetch if different session
    if (lastFetchedSessionId !== sessionId) return true;

    // Fetch if cache expired
    if (!lastFetchedAt || Date.now() - lastFetchedAt > CACHE_EXPIRATION_MS)
      return true;

    // Don't fetch if we have recent data for the same date
    return false;
  };

export const {
  topicsLoading,
  topicsReceived,
  topicsFailed,
  markTopicsStale,
  invalidateTopics,
  topicFiltersRefreshRequested,
  clearTopics,
} = todayTopicsSlice.actions;

export default todayTopicsSlice.reducer;
